// frontend/src/pages/PoliticaComunidade.jsx
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";

export default function PoliticaComunidade() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfUrl = `${process.env.PUBLIC_URL}/pdfs/politica-comunidade.pdf`;

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'politica-comunidade.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsGeneratingPDF(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; background: white; }
            .no-print { display: none !important; }
            .print-content { margin: 0; padding: 0.5cm; width: 100%; }
            .print-content h1 { font-size: 18pt; margin-bottom: 10pt; }
            .print-content h2 { font-size: 14pt; margin-top: 15pt; margin-bottom: 8pt; page-break-after: avoid; }
            .print-content p { font-size: 10pt; line-height: 1.4; margin-bottom: 6pt; page-break-inside: avoid; }
            .print-content ul { margin: 0 0 8pt 0; padding-left: 20pt; }
            .print-content li { font-size: 10pt; margin-bottom: 3pt; page-break-inside: avoid; }
            .print-content .section { page-break-inside: avoid; margin-bottom: 12pt; }
            .print-content .border-b { border-bottom: 0.5pt solid #ccc; }
            @page { size: A4; margin: 1.5cm; }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 pt-2 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                  alt="Brechó Vizinho"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Política da Comunidade</h1>
                  <p className="text-xs text-gray-500">Versão 1.0 | 15/06/2026</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#449879] text-white rounded-lg hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                >
                  {isGeneratingPDF ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Baixar PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 print:shadow-none print:p-0 print:bg-white">
            <div className="print-content prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800">
              
              {/* TÍTULO */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">POLÍTICA DA COMUNIDADE</h1>
                <p className="text-sm text-gray-500">Versão 1.0 | Data de Vigência: 15/06/2026</p>
                <div className="w-20 h-1 bg-[#449879] mx-auto mt-3 rounded-full print:bg-gray-400" />
              </div>

              {/* INTRODUÇÃO */}
              <p className="text-gray-600 leading-relaxed mb-4">
                O Brechó Vizinho foi criado para fortalecer a convivência entre vizinhos, incentivar o reaproveitamento de
                bens, promover a economia colaborativa e apoiar empreendedores locais.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Esta Política da Comunidade estabelece os princípios de convivência, respeito e responsabilidade que devem
                orientar a participação de todos os usuários da plataforma.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Ao utilizar o Brechó Vizinho, o usuário concorda em seguir estas diretrizes.
              </p>

              {/* 1. NOSSO PROPÓSITO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">1. NOSSO PROPÓSITO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Queremos construir uma comunidade baseada em:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Respeito;</li>
                  <li>Confiança;</li>
                  <li>Colaboração;</li>
                  <li>Boa convivência;</li>
                  <li>Solidariedade;</li>
                  <li>Responsabilidade.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho existe para aproximar pessoas, facilitar conexões locais e estimular relações positivas entre
                  moradores.
                </p>
              </div>

              {/* 2. RESPEITO ENTRE USUÁRIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">2. RESPEITO ENTRE USUÁRIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Todos os usuários devem tratar os demais com cordialidade e respeito.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Não serão tolerados:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Ofensas pessoais;</li>
                  <li>Ameaças;</li>
                  <li>Assédio;</li>
                  <li>Intimidação;</li>
                  <li>Discriminação;</li>
                  <li>Linguagem agressiva;</li>
                  <li>Conteúdo que promova ódio ou violência.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Discussões devem ser conduzidas de forma educada e respeitosa.
                </p>
              </div>

              {/* 3. HONESTIDADE NAS PUBLICAÇÕES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">3. HONESTIDADE NAS PUBLICAÇÕES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os anúncios devem refletir a realidade do item ou serviço divulgado.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário compromete-se a:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Informar corretamente as características do item;</li>
                  <li>Utilizar imagens verdadeiras;</li>
                  <li>Informar eventuais defeitos relevantes;</li>
                  <li>Não induzir outros usuários ao erro.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  A confiança da comunidade depende da honestidade de todos.
                </p>
              </div>

              {/* 4. RESPONSABILIDADE NAS NEGOCIAÇÕES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">4. RESPONSABILIDADE NAS NEGOCIAÇÕES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  As negociações são realizadas diretamente entre os usuários.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Recomenda-se:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Confirmar informações antes de concluir qualquer negociação;</li>
                  <li>Verificar pessoalmente os itens sempre que possível;</li>
                  <li>Adotar cuidados de segurança em encontros presenciais;</li>
                  <li>Registrar acordos de forma clara.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho não participa das negociações realizadas entre os usuários.
                </p>
              </div>

              {/* 5. CONTEÚDOS PROIBIDOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">5. CONTEÚDOS PROIBIDOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Não é permitido publicar conteúdos que envolvam:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Atividades ilegais;</li>
                  <li>Produtos proibidos por lei;</li>
                  <li>Produtos falsificados;</li>
                  <li>Conteúdo pornográfico;</li>
                  <li>Material que incentive violência;</li>
                  <li>Discurso de ódio;</li>
                  <li>Fraudes;</li>
                  <li>Informações enganosas;</li>
                  <li>Spam;</li>
                  <li>Conteúdo ofensivo.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá remover tais conteúdos imediatamente.
                </p>
              </div>

              {/* 6. SPAM E USO ABUSIVO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">6. SPAM E USO ABUSIVO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Não é permitido:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Publicar repetidamente o mesmo anúncio;</li>
                  <li>Criar múltiplas contas para burlar limitações da plataforma;</li>
                  <li>Enviar mensagens em massa;</li>
                  <li>Utilizar sistemas automatizados para gerar conteúdo;</li>
                  <li>Praticar qualquer atividade que prejudique o funcionamento da plataforma.</li>
                </ul>
              </div>

              {/* 7. EMPREENDEDORES E NEGÓCIOS LOCAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">7. EMPREENDEDORES E NEGÓCIOS LOCAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho apoia o empreendedorismo local e incentiva a participação de moradores que ofereçam
                  produtos ou serviços.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os empreendedores devem:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Agir com transparência;</li>
                  <li>Fornecer informações verdadeiras;</li>
                  <li>Respeitar os demais usuários;</li>
                  <li>Cumprir os compromissos assumidos.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  A divulgação de atividades comerciais deverá respeitar as regras e formatos disponibilizados pela plataforma.
                </p>
              </div>

              {/* 8. DENÚNCIAS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">8. DENÚNCIAS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os usuários podem denunciar conteúdos, anúncios ou comportamentos inadequados praticados por outros
                  usuários que violem esta Política.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  As denúncias serão analisadas pela equipe responsável.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O envio de denúncias falsas ou de má-fé também poderá resultar em medidas administrativas.
                </p>
              </div>

              {/* 9. MEDIDAS APLICÁVEIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">9. MEDIDAS APLICÁVEIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Em caso de descumprimento desta Política, o Brechó Vizinho poderá adotar uma ou mais das seguintes
                  medidas:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Advertência;</li>
                  <li>Remoção de conteúdo;</li>
                  <li>Suspensão temporária de funcionalidades;</li>
                  <li>Suspensão temporária da conta;</li>
                  <li>Bloqueio permanente da conta;</li>
                  <li>Exclusão definitiva da conta.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  As medidas poderão ser aplicadas sem aviso prévio quando necessário para proteção da comunidade.
                </p>
              </div>

              {/* 10. SEGURANÇA DA COMUNIDADE */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">10. SEGURANÇA DA COMUNIDADE</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Para preservar a segurança dos moradores, o Brechó Vizinho poderá adotar mecanismos de validação de
                  usuários e monitoramento de atividades suspeitas.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Os usuários comprometem-se a colaborar com os procedimentos de verificação quando solicitados.
                </p>
              </div>

              {/* 11. CONVIVÊNCIA DIGITAL */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">11. CONVIVÊNCIA DIGITAL</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma deve ser utilizada com o mesmo respeito esperado em uma convivência presencial entre vizinhos.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Antes de publicar, comentar ou interagir, recomendamos considerar se o conteúdo contribui positivamente
                  para a comunidade.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Nosso objetivo é criar um ambiente acolhedor, seguro e útil para todos.
                </p>
              </div>

              {/* 12. ALTERAÇÕES DESTA POLÍTICA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">12. ALTERAÇÕES DESTA POLÍTICA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esta Política poderá ser atualizada periodicamente para acompanhar a evolução da plataforma e das
                  necessidades da comunidade.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A versão mais recente estará sempre disponível no Brechó Vizinho.
                </p>
              </div>

              {/* 13. DISPOSIÇÕES FINAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">13. DISPOSIÇÕES FINAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A utilização da plataforma implica concordância com esta Política da Comunidade.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O descumprimento destas diretrizes poderá resultar na aplicação das medidas previstas neste documento.
                </p>
              </div>

              {/* ASSINATURA FINAL */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Última atualização: 15/06/2026<br />
                  Versão: 1.0<br />
                  Brechó Vizinho<br />
                  E-mail: <span className="text-[#449879] font-medium">info@brechovizinho.com.br</span>
                </p>
              </div>
            </div>
          </div>

          {/* Botão voltar ao topo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="no-print fixed bottom-20 right-4 bg-[#449879] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-colors z-40"
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}